import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { priceBuckets, products } from "../data/products";
import type { Category } from "../types/product";

export type SortOption = "aanbevolen" | "prijs-op" | "prijs-af" | "naam";

export interface ShopFilters {
  categories: Category[];
  types: string[];
  colors: string[];
  prices: string[];
  personalizable: boolean;
}

const empty: ShopFilters = { categories: [], types: [], colors: [], prices: [], personalizable: false };
const allCategories: Category[] = ["Verlichting", "Woondecoratie", "Gepersonaliseerd"];

export function useShopFilters() {
  const [params, setParams] = useSearchParams();
  const categoryParam = params.get("categorie");
  const query = params.get("q") ?? "";
  const [filters, setFilters] = useState<ShopFilters>(empty);
  const [sort, setSort] = useState<SortOption>("aanbevolen");

  useEffect(() => {
    const cat = allCategories.find((c) => c === categoryParam);
    setFilters({ ...empty, categories: cat ? [cat] : [] });
  }, [categoryParam]);

  const toggle = (key: "categories" | "types" | "colors" | "prices", value: string) => {
    setFilters((f) => {
      const list = f[key] as string[];
      const next = list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
      return { ...f, [key]: next };
    });
  };

  const setPersonalizable = (value: boolean) => setFilters((f) => ({ ...f, personalizable: value }));

  const clearAll = () => {
    setFilters(empty);
    if (categoryParam || query) setParams({});
  };

  const clearQuery = () => {
    const next = new URLSearchParams(params);
    next.delete("q");
    setParams(next);
  };

  const options = useMemo(
    () => ({
      categories: allCategories.map((c) => ({ value: c, count: products.filter((p) => p.category === c).length })),
      types: Array.from(new Set(products.map((p) => p.type))).map((t) => ({ value: t, count: products.filter((p) => p.type === t).length })),
      colors: Array.from(new Set(products.flatMap((p) => p.colors ?? []))),
    }),
    [],
  );

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = products.filter((p) => {
      if (q && !`${p.name} ${p.title} ${p.type} ${p.category}`.toLowerCase().includes(q)) return false;
      if (filters.categories.length && !filters.categories.includes(p.category)) return false;
      if (filters.types.length && !filters.types.includes(p.type)) return false;
      if (filters.colors.length && !(p.colors ?? []).some((c) => filters.colors.includes(c))) return false;
      if (filters.personalizable && !p.personalizable) return false;
      if (filters.prices.length) {
        const match = priceBuckets.some((b) => filters.prices.includes(b.id) && p.price >= b.min && p.price < b.max);
        if (!match) return false;
      }
      return true;
    });
    if (sort === "prijs-op") return [...list].sort((a, b) => a.price - b.price);
    if (sort === "prijs-af") return [...list].sort((a, b) => b.price - a.price);
    if (sort === "naam") return [...list].sort((a, b) => a.name.localeCompare(b.name, "nl"));
    return list;
  }, [filters, sort, query]);

  const activeCount =
    filters.categories.length + filters.types.length + filters.colors.length + filters.prices.length + (filters.personalizable ? 1 : 0);

  return { filters, toggle, setPersonalizable, clearAll, clearQuery, query, sort, setSort, options, results, activeCount };
}
